import { useQueryClient } from "@tanstack/react-query";
import FetchClients, { ClientOutput } from "../client";
import { throwBackpackResponseError } from "../errors";
import {
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";

export type CapitalPlanStatus = ClientOutput["projects"]["CapitalPlanStatus"];
export type ProjectCapitalPlanCompletionStatus =
  ClientOutput["projects"]["ProjectCapitalPlanCompletionStatus"];

export const CAPITAL_PLAN_COMPLETION_STATUS_KEY =
  "projects/capital-plan-completion-status";

const onboarding = {
  useQueryCapitalPlanCompletionStatus,
  mutations: {
    useUpsertCapitalPlanCompletionStatus,
    useUpsertAdditionalProjectOptOut,
  },
} as const;

export default onboarding;

// endpoint requires {feature: "Projects", access: "read"}
function useQueryCapitalPlanCompletionStatus(siteId: number) {
  return useQueryWithAccessCheckLog(
    ["Projects", "read"],
    async (checkFeatureAccessHeader) => {
      const { data, error, response } = await FetchClients.projects.GET(
        "/capital_plan_completion_status",
        {
          params: { query: { site_id: siteId } },
          headers: checkFeatureAccessHeader,
        }
      );

      // a site that has never been asked the capital plan question has no record
      if (response.status === 404) return null;
      if (data) return data;
      throwBackpackResponseError(error);
    },
    { queryKey: [CAPITAL_PLAN_COMPLETION_STATUS_KEY, siteId] }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
function useUpsertCapitalPlanCompletionStatus({
  siteId,
  onSettled,
}: {
  siteId: number;
  onSettled?: () => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (status: CapitalPlanStatus, checkFeatureAccessHeader) => {
      const { error } = await FetchClients.projects.PUT(
        "/capital_plan_completion_status",
        {
          params: { query: { site_id: siteId, status } },
          headers: checkFeatureAccessHeader,
        }
      );
      if (error) throwBackpackResponseError(error);
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: [CAPITAL_PLAN_COMPLETION_STATUS_KEY, siteId],
        });
        await onSettled?.();
      },
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
function useUpsertAdditionalProjectOptOut({
  siteId,
  onSettled,
}: {
  siteId: number;
  onSettled?: () => void | Promise<void>;
}) {
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (optOut: boolean, checkFeatureAccessHeader) => {
      const { error } = await FetchClients.projects.PUT(
        "/additional_project_opt_out",
        {
          params: { query: { site_id: siteId, opt_out: optOut } },
          headers: checkFeatureAccessHeader,
        }
      );
      if (error) throwBackpackResponseError(error);
    },
    { onSettled }
  );
}
